/**
 * Application Error Types
 *
 * This module defines all error types used throughout the application.
 * All errors are explicitly typed for exhaustive handling.
 */

/**
 * Known application errors - these are expected failure modes
 *
 * All errors support optional cause and stack fields for error chaining:
 * - cause: The underlying error that caused this error (enables error chains)
 * - stack: Stack trace for debugging (automatically captured)
 */
export type KnownAppError =
  | {
      type: "NetworkError";
      message: string;
      url?: string;
      cause?: unknown;
      stack?: string;
    }
  | { type: "Unauthorized"; userId?: string; cause?: unknown; stack?: string }
  | {
      type: "ValidationError";
      fields: string[];
      messages: Record<string, string>;
      cause?: unknown;
      stack?: string;
    }
  | {
      type: "NotFound";
      resource: string;
      id: string;
      cause?: unknown;
      stack?: string;
    }
  | {
      type: "AgeRestriction";
      minAge: number;
      providedAge: number;
      cause?: unknown;
      stack?: string;
    }
  | {
      type: "FirebaseError";
      code: string;
      message: string;
      cause?: unknown;
      stack?: string;
    }
  | {
      type: "MissingBirthdate";
      provider: string;
      cause?: unknown;
      stack?: string;
    };

/**
 * Unexpected errors - bugs or unhandled conditions
 *
 * These errors ALWAYS include:
 * - error: The raw error that occurred
 * - context: Where/why the error occurred
 * - stack: Stack trace for debugging
 */
export type UnexpectedError = {
  type: "Unexpected";
  error: unknown;
  context?: string;
  cause?: unknown;
  stack?: string;
};

/**
 * All application errors
 */
export type AppError = KnownAppError | UnexpectedError;

/**
 * Helper to create NetworkError
 */
export function createNetworkError(params: {
  message: string;
  url?: string;
  cause?: unknown;
}): AppError {
  const { message, url, cause } = params;
  const stack = new Error().stack;
  return { type: "NetworkError", message, url, cause, stack };
}

/**
 * Helper to create ValidationError
 */
export function createValidationError(params: {
  fields: string[];
  messages: Record<string, string>;
}): AppError {
  const { fields, messages } = params;
  return { type: "ValidationError", fields, messages };
}

/**
 * Helper to create NotFound error
 */
export function createNotFoundError(params: {
  resource: string;
  id: string;
}): AppError {
  const { resource, id } = params;
  return { type: "NotFound", resource, id };
}

/**
 * Helper to create AgeRestriction error
 */
export function createAgeRestrictionError(params: {
  minAge: number;
  providedAge: number;
}): AppError {
  const { minAge, providedAge } = params;
  return { type: "AgeRestriction", minAge, providedAge };
}

/**
 * Helper to create FirebaseError
 */
export function createFirebaseError(params: {
  code: string;
  message: string;
  cause?: unknown;
}): AppError {
  const { code, message, cause } = params;
  const stack = new Error().stack;
  return { type: "FirebaseError", code, message, cause, stack };
}

/**
 * Helper to create MissingBirthdate error
 */
export function createMissingBirthdateError(params: {
  provider: string;
}): AppError {
  const { provider } = params;
  return { type: "MissingBirthdate", provider };
}

/**
 * Helper to create Unexpected error
 *
 * CRITICAL: Always provide a descriptive context to help debugging.
 * Context should describe WHERE the error occurred and WHAT was being attempted.
 *
 * @example
 * createUnexpectedError({
 *   error: err,
 *   context: 'Loading user settings from Firestore for userId=123'
 * })
 */
export function createUnexpectedError(params: {
  error: unknown;
  context?: string;
  cause?: unknown;
}): AppError {
  const { error, context, cause } = params;
  const stack = new Error().stack;
  return { type: "Unexpected", error, context, cause, stack };
}

/**
 * Map third-party errors to AppError
 * Use this at boundaries to wrap external errors
 */
export function mapFirebaseError(error: unknown): AppError {
  if (error instanceof Error) {
    // Check if it's a Firebase error with a code
    if ("code" in error) {
      const code = (error as { code: string }).code;
      return createFirebaseError({
        code,
        message: error.message,
        cause: error,
      });
    }

    // Generic error - treat as unexpected
    return createUnexpectedError({
      error,
      context: "Firebase operation",
      cause: error,
    });
  }

  // Not even an Error object
  return createUnexpectedError({ error, context: "Firebase operation" });
}

/**
 * Map fetch errors to AppError
 * Use this when wrapping fetch/axios calls
 */
export function mapFetchError(params: {
  error: unknown;
  url?: string;
}): AppError {
  const { error, url } = params;

  if (error instanceof TypeError) {
    // Network errors are typically TypeError
    return createNetworkError({ message: error.message, url, cause: error });
  }

  if (error instanceof Error) {
    return createNetworkError({ message: error.message, url, cause: error });
  }

  return createNetworkError({
    message: "Unknown network error",
    url,
    cause: error,
  });
}

/**
 * Extract detailed error information from an AppError for debugging
 * This includes the full error chain and stack traces
 *
 * @param error - The AppError to extract details from
 * @returns Object with message, details array, and original error
 */
export function getErrorDetails(error: AppError): {
  message: string;
  details: string[];
  originalError: unknown;
} {
  const details: string[] = [];

  // Add primary error info
  details.push(`Type: ${error.type}`);

  // Add type-specific details
  if (error.type === "FirebaseError") {
    details.push(`Code: ${error.code}`);
    details.push(`Message: ${error.message}`);
  } else if (error.type === "NetworkError") {
    details.push(`Message: ${error.message}`);
    if (error.url) {
      details.push(`URL: ${error.url}`);
    }
  } else if (error.type === "ValidationError") {
    details.push(`Fields: ${error.fields.join(", ")}`);
    details.push(`Messages: ${JSON.stringify(error.messages, null, 2)}`);
  } else if (error.type === "Unexpected") {
    if (error.context) {
      details.push(`Context: ${error.context}`);
    }
    if (error.error instanceof Error) {
      details.push(`Error: ${error.error.message}`);
      if (error.error.stack) {
        details.push(`Stack: ${error.error.stack}`);
      }
    } else {
      details.push(`Error: ${JSON.stringify(error.error)}`);
    }
  }

  // Add stack trace if available
  if ("stack" in error && error.stack) {
    details.push(`Stack: ${error.stack}`);
  }

  // Add cause chain
  if ("cause" in error && error.cause) {
    details.push(`--- Caused by ---`);
    if (error.cause instanceof Error) {
      details.push(`Cause: ${error.cause.message}`);
      if (error.cause.stack) {
        details.push(`Stack: ${error.cause.stack}`);
      }
    } else {
      details.push(`Cause: ${JSON.stringify(error.cause)}`);
    }
  }

  // Determine the message to display
  let message = "An error occurred";
  if (error.type === "FirebaseError") {
    message = error.message;
  } else if (error.type === "NetworkError") {
    message = error.message;
  } else if (error.type === "ValidationError") {
    message = `Validation failed: ${error.fields.join(", ")}`;
  } else if (error.type === "Unexpected" && error.context) {
    message = `Unexpected error: ${error.context}`;
  }

  const originalError =
    error.type === "Unexpected"
      ? error.error
      : "cause" in error
        ? error.cause
        : error;

  return { message, details, originalError };
}

/**
 * Format an error for display to users
 * In development/test: Shows full details
 * In production: Shows safe message with option to reveal details
 *
 * @param error - The AppError to format
 * @param showDetails - Whether to show full details (default: checks VITE_SHOW_ERROR_DETAILS env var)
 * @returns Formatted error message
 */
export function formatErrorForDisplay(
  error: AppError,
  showDetails?: boolean
): string {
  // Only show details if explicitly enabled via environment variable
  // This allows E2E tests to see full error details without affecting unit tests
  const shouldShowDetails =
    showDetails ?? import.meta.env.VITE_SHOW_ERROR_DETAILS === "true";
  const { message, details } = getErrorDetails(error);

  if (shouldShowDetails) {
    // Show full details when explicitly enabled
    return `${message}\n\n=== Error Details ===\n${details.join("\n")}`;
  } else {
    // Show safe message only
    return message;
  }
}
